import React, { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useCompanyOptions } from '../../hooks/useCompanyOptions';
import ContactSelector from './ContactSelector';
import type { Job, NewJob, JobStatus } from '../../types/job';
import { useJobContacts } from '../../hooks/useJobContacts';

interface JobFormProps {
  job?: Job;
  onSuccess: () => void;
  onCancel: () => void;
}

const STATUS_OPTIONS: { value: JobStatus; label: string }[] = [
  { value: 'prospected', label: 'Prospected' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'interviewing', label: 'Interviewing' },
  { value: 'negotiating', label: 'Negotiating' },
  { value: 'offer', label: 'Offer Established' },
  { value: 'archived', label: 'Archived' }
];

export default function JobForm({ job, onSuccess, onCancel }: JobFormProps) {
  const { user } = useAuth();
  const { options: companyOptions } = useCompanyOptions();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [formData, setFormData] = useState<NewJob>({
    company_id: job?.company_id || '',
    position: job?.position || '',
    description: job?.description || '',
    salary_range: job?.salary_range || '',
    location: job?.location || '',
    job_posting_url: job?.job_posting_url || '',
    status: job?.status || 'prospected',
    notes: job?.notes || ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !formData.company_id) return;

    setSaving(true);
    setError(null);

    try {
      if (job) {
        // Update existing job
        const { error: updateError } = await supabase
          .from('nrm_jobs')
          .update({
            ...formData,
            updated_at: new Date().toISOString()
          })
          .eq('id', job.id);

        if (updateError) throw updateError;
      } else {
        // Create new job
        const { data: newJob, error: createError } = await supabase
          .from('nrm_jobs')
          .insert([{
            ...formData,
            user_id: user.id
          }])
          .select()
          .single();

        if (createError) throw createError;

        // Add job contacts
        if (selectedContacts.length > 0) {
          const contacts = selectedContacts.map(contactId => ({
            job_id: newJob.id,
            contact_id: contactId,
            role: 'referral' // Default role
          }));

          const { error: contactsError } = await supabase
            .from('nrm_job_contacts')
            .insert(contacts);

          if (contactsError) throw contactsError;
        }
      }

      onSuccess();
    } catch (err) {
      setError('Failed to save opportunity. Please try again.');
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="company_id" className="block text-sm font-medium text-gray-300 mb-1">
            Company <span className="text-red-500">*</span>
          </label>
          <select
            id="company_id"
            name="company_id"
            required
            value={formData.company_id}
            onChange={handleChange}
            className="input w-full"
          >
            <option value="">Select company...</option>
            {companyOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="position" className="block text-sm font-medium text-gray-300 mb-1">
            Position <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="position"
            name="position"
            required
            value={formData.position}
            onChange={handleChange}
            className="input w-full"
          />
        </div>

        <div>
          <label htmlFor="location" className="block text-sm font-medium text-gray-300 mb-1">
            Location
          </label>
          <input
            type="text"
            id="location"
            name="location"
            value={formData.location}
            onChange={handleChange}
            className="input w-full"
          />
        </div>

        <div>
          <label htmlFor="salary_range" className="block text-sm font-medium text-gray-300 mb-1">
            Salary Range
          </label>
          <input
            type="text"
            id="salary_range"
            name="salary_range"
            value={formData.salary_range}
            onChange={handleChange}
            className="input w-full"
          />
        </div>

        <div>
          <label htmlFor="job_posting_url" className="block text-sm font-medium text-gray-300 mb-1">
            Job Posting URL
          </label>
          <input
            type="url"
            id="job_posting_url"
            name="job_posting_url"
            value={formData.job_posting_url}
            onChange={handleChange}
            className="input w-full"
          />
        </div>

        <div>
          <label htmlFor="status" className="block text-sm font-medium text-gray-300 mb-1">
            Status <span className="text-red-500">*</span>
          </label>
          <select
            id="status"
            name="status"
            required
            value={formData.status}
            onChange={handleChange}
            className="input w-full"
          >
            {STATUS_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-300 mb-1">
          Description
        </label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows={3}
          className="input w-full"
        />
      </div>

      <div>
        <label htmlFor="notes" className="block text-sm font-medium text-gray-300 mb-1">
          Notes
        </label>
        <textarea
          id="notes"
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          rows={3}
          className="input w-full"
        />
      </div>

      {!job && (
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Associated Contacts
          </label>
          <ContactSelector
            selectedContacts={selectedContacts}
            onSelectContact={(contactId) => setSelectedContacts(prev => [...prev, contactId])}
            onRemoveContact={(contactId) => setSelectedContacts(prev => prev.filter(id => id !== contactId))}
          />
        </div>
      )}

      {error && (
        <div className="text-red-500 text-sm">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-4">
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-secondary"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="btn btn-primary"
        >
          {saving ? 'Saving...' : job ? 'Update Opportunity' : 'Add Opportunity'}
        </button>
      </div>
    </form>
  );
}